import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Input as ReactstrapInput, InputProps } from 'reactstrap';
import { TranslateFn } from '@blocs.i18n';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';

type InputOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

const validateOptionProp = ({ fieldName, options }: { fieldName: string; options: InputOption[] | undefined }) => {
  const arr = (Array.isArray(options) ? options : []).filter((opt) => {
    return typeof opt === 'object' && opt !== null && !Array.isArray(opt);
  });
  const arrValues = [];

  return arr.filter((opt, index) => {
    if (typeof opt.value !== 'string') {
      console.warn(`${fieldName}: option with index ${index} has a value that is not a string`);
      return false;
    }

    if (typeof opt.label !== 'string') {
      console.warn(`${fieldName}: option with index ${index} has a label that is not a string`);
      return false;
    }

    if (arrValues.includes(opt.value)) {
      console.warn(`${fieldName}: found multiple options with value ${opt.value}`);
      return false;
    }

    arrValues.push(opt.value);

    return true;
  });
};

const StyledReactstrapInput = styled(ReactstrapInput)<{
  $error: boolean;
  $noPadding: boolean;
  $hideNumberButtons: boolean;
}>`
  background: ${({ theme }) => theme.designSystem.bgColors.input};
  color: ${({ theme }) => theme.designSystem.textColors.default};
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.input)};
  outline: none;
  ${({ $noPadding }) => ($noPadding ? 'padding: 0;' : '')}

  ${({ $hideNumberButtons }) => {
    if (!$hideNumberButtons) {
      return '';
    }

    return `
      ::-webkit-inner-spin-button,
      ::-webkit-outer-spin-button {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        margin: 0;
      }`;
  }}

  &&:focus,
  &&:focus-visible {
    background: ${({ theme }) => theme.designSystem.bgColors.input};
    color: ${({ theme }) => theme.designSystem.textColors.default};
    box-shadow: none;
    border: 1px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.inputFocus};
    outline: 4px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.inputFocusError : theme.designSystem.borderColors.inputFocusOutline};
  }

  &&:disabled {
    box-shadow: none;
    background: ${({ theme }) => theme.designSystem.bgColors.inputDisabled};
    border: 1px solid ${({ theme }) => theme.designSystem.borderColors.input};
  }
`;

/** Types */
type ReactstrapInputProps = Pick<
  InputProps,
  'bsSize' | 'valid' | 'invalid' | 'innerRef' | 'plaintext' | 'addon' | 'type' | 'multiple'
>;
type HtmlInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur' | 'value' | 'multiple'>;

export type BaseInputProps = ReactstrapInputProps &
  HtmlInputProps & {
    /**
     * callback invoked with the value of the text field
     */
    onChange?: (newValue: string) => void;
    /** callback invoked when the input loses focus */
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    /**
     * value of this input
     */
    value: string;
    /** test id value */
    'data-testid'?: string;
    /** qa id value */
    'data-qa-id'?: string;
    /** Recurly.js hosted-field binding — set on inputs that Recurly's tokenizer scans by this attribute */
    'data-recurly'?: string;
    /** name attribute for the input */
    name: string;
    /** number of rows to show in for the height of the textarea, only passed into the input if type="textarea" */
    rows?: number;
    /** options for select inputs, only passed into the input if type="select" */
    options?: Array<{ value: string; label: string; disabled?: boolean }>;
    /** remove all padding from the input */
    noPadding?: boolean;
    /** hide the number increment buttons when type="number" */
    hideNumberButtons?: boolean;
  };

/**
 * A simple, controlled input
 * @name BaseInput
 * @description this is to be used as a base for other input components, do not import this directly into application code
 * it does not include a label, help text, validation or other form field features
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <BaseInput value={value} onChange={setSelectedValue} />
 */
export const BaseInput: FC<BaseInputProps> = ({
  onBlur,
  onChange,
  'data-testid': testId,
  'data-qa-id': qaId,
  type = 'text',
  value,
  name,
  id,
  rows,
  options: optionsProp,
  noPadding = false,
  hideNumberButtons = false,
  ...inputProps
}) => {
  const normalizedType = ['text', 'password', 'number', 'search', 'email', 'tel', 'textarea', 'url', 'select'].includes(
    type
  )
    ? type
    : 'text';

  const options = useMemo(() => {
    if (type !== 'select' || !Array.isArray(optionsProp)) {
      return [];
    }

    return validateOptionProp({ fieldName: name, options: optionsProp });
  }, [type, optionsProp, name]);

  return (
    <StyledReactstrapInput
      {...inputProps}
      onBlur={onBlur}
      id={id ?? name}
      name={name}
      data-testid={testId}
      data-qa-id={qaId}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      type={normalizedType}
      value={value === undefined ? '' : value}
      size={undefined}
      bsSize={undefined}
      $error={inputProps?.invalid}
      $noPadding={noPadding}
      $hideNumberButtons={hideNumberButtons}
      rows={type === 'textarea' && typeof rows === 'number' ? rows : undefined}
    >
      {type === 'select'
        ? options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))
        : null}
    </StyledReactstrapInput>
  );
};

export interface WrappedBaseInputProps {
  wrapperProps?: FieldWrapperProps;
  inputProps?: BaseInputProps;
}

/**
 * A simple, controlled input with the standard form field wrapper
 * @name WrappedBaseInput
 * @description this is to be used as a base for other input components, do not import this directly into application code
 * it should be setup with a formik <Field /> around it which maps value, onChange, name, validation, etc into the props
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <WrappedBaseInput
 *    inputProps={{ value, onChange: setSelectedValue, type: 'text', name: 'input' }}
 *    wrapperProps={{ label: 'My Input', helpText: 'Please Fill This Out', id: 'input' }}
    />
 */
export const WrappedBaseInput: FC<WrappedBaseInputProps> = ({ wrapperProps = {}, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <BaseInput {...inputProps} />
    </FieldWrapper>
  );
};

export const getHelpTextValue = ({
  maxLength,
  helpTextProp,
  currentValue,
  t,
}: {
  maxLength?: number;
  helpTextProp?: string;
  currentValue?: string;
  t: TranslateFn;
}) => {
  let maxLenHelpText = '';
  if (typeof maxLength === 'number' && currentValue?.length >= maxLength) {
    return t('common:form.info.maxLengthReached', { maxLength });
  }

  if (typeof maxLength === 'number' && currentValue?.length < maxLength) {
    maxLenHelpText = t('common:form.info.maxLength', { maxLength });
  }

  return helpTextProp || maxLenHelpText;
};

// formik can provide the error for a field as a string, or array of strings, possibly an object of key/value pairs for nested fields as well
// this helper attempts to consolidate the various formats into a single stringified output
export const normalizeFormikError = (error: string | string[] | undefined) => {
  if (!error) {
    // falsy values get returned back as-is
    return error as undefined;
  }

  // if it's anything other than an array, just return it
  // this handles the case where formik returns a string or object with key / values
  if (!Array.isArray(error)) {
    return error?.toString();
  }

  // if its an array, filter out any falsy / empty values, then join the remaining ones together
  return error?.filter((e) => typeof e === 'string')?.join(', ');
};
